import { useEffect, useState } from 'react';
import { Input, InputGroup, InputGroupText, Label } from 'reactstrap';
import { useTranslation } from '../../store/context/TranslationContext';
import { INPUT_AUTOFILL_PROPS } from './autofillProps';
import PreviousValue from './PreviousValue';
import { DEFAULT_NUMERIC_MAX_VALUE, sanitizeNumericInputValue } from './numericValidation';
import { buildAutomationChildTestId, resolveAutomationTestId } from './testId';

const formatNumberUS = (number) => {
    if (number === "" || number === null || isNaN(number)) return "";
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(number);
};

const formatInputGroupTextDisplay = (value = "") => {
    const normalized = String(value ?? "").trim().toLowerCase();
    if (normalized === "usd" || normalized === "$") return "$";
    if (normalized === "count" || normalized === "#") return "#";
    return value;
};

const NumberField = ({
    id,
    isRequired,
    label = "",
    value,
    onChange,
    placeholder = "",
    showInputGroupText = false,
    inputGroupText = "USD",
    asLabel,
    error,
    showErr,
    isDisabled,
    prevValue,
    description,
    variant = "",
    onCommit,
    inputRegex,
    maxValue = DEFAULT_NUMERIC_MAX_VALUE,
    minValue,
    textAlign = "right",
}) => {
    const { t } = useTranslation();
    const isPlain = variant === "plain";
    const fieldTestId = resolveAutomationTestId(id, label);
    const [localError, setLocalError] = useState("");
    const resolvedTextAlign = textAlign === "left" ? "left" : "right";
    const isLeftAligned = resolvedTextAlign === "left";

    useEffect(() => {
        setLocalError("");
    }, [value]);

    const containerClass = `inputfield__container number-field-container ${isPlain ? "m-0 p-0" : ""}`;
    const resolvedError = localError || error;
    const shouldDisplayError = Boolean(localError) || (!isDisabled && Boolean(error) && Boolean(showErr));

    const innerClass = [
        "inputfield__innercontainer",
        "p-0",
        "overflow-hidden",
        prevValue?.has ? "is-changed" : "",
        isDisabled ? "is-disabled" : "",
        isPlain ? "rounded-0" : "",
        shouldDisplayError && isPlain ? "no-value-error" : ""
    ].filter(Boolean).join(" ");
    const resolvedInputGroupText = formatInputGroupTextDisplay(inputGroupText);
    const inputClassName = [
        "fv__input-group-input",
        resolvedTextAlign === "left" ? "fv__input-group-input--align-left" : "fv__input-group-input--align-right",
    ].join(" ");

    if (asLabel) return <>{formatNumberUS(value)}</>;

    return (
        <div className={containerClass} data-testid={fieldTestId}>
            {!isPlain ? (
                <Label className="fv__input-field-label" data-testid={buildAutomationChildTestId(fieldTestId, "label")}>
                    {t(label)}{isRequired && <span style={{ color: 'red' }}>*</span>}
                </Label>
            ) : null}

            <InputGroup className={innerClass}>
                {showInputGroupText && (
                    <InputGroupText className="fv__input-group-text" data-testid={buildAutomationChildTestId(fieldTestId, "prefix")}>
                        {resolvedInputGroupText === inputGroupText ? t(resolvedInputGroupText) : resolvedInputGroupText}
                    </InputGroupText>
                )}
                <Input
                    id={id}
                    data-testid={buildAutomationChildTestId(fieldTestId, "input")}
                    {...INPUT_AUTOFILL_PROPS}
                    onChange={(e) => {
                        const nextValue = sanitizeNumericInputValue(e.target.value, {
                            uiType: 'numberField',
                            label,
                            uiRegex: inputRegex,
                            maxValue,
                            minValue,
                        });

                        if (!nextValue.isValid) {
                            setLocalError(nextValue.error);
                            return;
                        }

                        setLocalError("");
                        onChange(nextValue.numericValue);
                    }}
                    onBlur={() => onCommit?.(value)}
                    placeholder={t(placeholder)}
                    value={formatNumberUS(value)}
                    className={inputClassName}
                    type="text"
                    disabled={isDisabled}
                    readOnly={isDisabled}
                />
            </InputGroup>

            {description && !isPlain ? (
                <em
                    className={`inputfield__desc d-block mt-1 ${isLeftAligned ? "text-start" : "text-end me-3"}`}
                    data-testid={buildAutomationChildTestId(fieldTestId, "description")}
                >
                    {description}
                </em>
            ) : null}

            {!isPlain ? (
                <div className={isLeftAligned ? "text-start" : "text-end"}>
                    <PreviousValue
                        value={formatNumberUS(prevValue?.value)}
                        show={prevValue?.has}
                        testId={buildAutomationChildTestId(fieldTestId, "previous")}
                    />
                </div>
            ) : null}

            {!isDisabled && shouldDisplayError && !isPlain ? (
                <em
                    className={`inputfield__error d-block ${isLeftAligned ? "text-start" : "text-end"}`}
                    data-testid={buildAutomationChildTestId(fieldTestId, "error")}
                >
                    {resolvedError ? t(resolvedError) : "Please enter Something"}
                </em>
            ) : null}
        </div>
    )
}

export default NumberField;
